/**
 * Simple event system for translation status updates.
 * 
 * Used to notify book detail pages when a translation completes,
 * and to trigger polling in the progress widget when a translation starts.
 */

interface TranslationCompletePayload {
  bookId: string
  translationId: string
  status: 'completed' | 'failed'
}

type TranslationEventCallback = (payload: TranslationCompletePayload) => void
type TranslationStartedCallback = () => void

class TranslationEventEmitter {
  private listeners: Set<TranslationEventCallback> = new Set()
  private startedListeners: Set<TranslationStartedCallback> = new Set()

  /**
   * Subscribe to translation complete events
   */
  subscribe(callback: TranslationEventCallback): () => void {
    this.listeners.add(callback)
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback)
    }
  }

  /**
   * Subscribe to translation started events.
   * Used by the progress widget to resume polling.
   */
  subscribeToStarted(callback: TranslationStartedCallback): () => void {
    this.startedListeners.add(callback)
    return () => {
      this.startedListeners.delete(callback)
    }
  }

  /**
   * Emit a translation complete event.
   */
  emit(payload: TranslationCompletePayload): void {
    this.listeners.forEach(callback => callback(payload))
  }

  /**
   * Emit a translation started event.
   */
  emitStarted(): void {
    this.startedListeners.forEach(callback => callback())
  }
}

// Singleton instance
export const translationEvents = new TranslationEventEmitter()

/**
 * Helper function to notify that a translation has completed.
 * Call this when a translation job finishes (success or failure).
 */
export function notifyTranslationComplete(bookId: string, translationId: string, status: 'completed' | 'failed'): void {
  translationEvents.emit({ bookId, translationId, status })
}

/**
 * Helper function to notify that a translation has been queued/started.
 * Call this when a user starts a new translation to resume polling.
 */
export function notifyTranslationStarted(): void {
  translationEvents.emitStarted()
}